/**
 * Format: Option
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API options response.
 *
 */
import { formatMoney } from '@we-make-websites/theme-currency';

import { stripTrailingParenthetical } from '~/format/format-variant';

/**
 * Format product options array.
 * @param {Object} product - Product response.
 * @param {Array} variants - Formatted variants response.
 * @returns {Array}
 */
export function formatOptions(product, variants) {
  if (!product.options) {
    return [];
  }

  return product.options
    .map((option, index) => formatOption(option, { index, product, variants }))
    .sort(sortOptionNames)
    .map((option, index) => {
      return {
        ...option,
        index,
      };
    });
}

/**
 * Format product option.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/ProductOption
 * @param {Object} option - Option response.
 * @param {Object} [data] - Additional data.
 * @param {Number} [data.index] - Forloop index.
 * @param {Object} [data.product] - Product response.
 * @param {Array} [data.variants] - Formatted variants response.
 * @returns {Object}
 */
function formatOption(option, { index, product, variants } = {}) {
  const values = [
    ...new Set(option.values.map((value) => stripTrailingParenthetical(value))),
  ];

  const formattedOption = {
    labels: values,
    name: option.name,
    originalIndex: index,
    values,
  };

  /**
   * If gift card then show price as label to support multi-currency.
   */
  if (product.giftCard && variants.length) {
    formattedOption.labels = option.values.map((_, valueIndex) => {
      return formatMoney(
        variants[valueIndex].price.amount,
        cnvs.store.moneyFormat,
      );
    });
  }

  return formattedOption;
}

/**
 * Sort option names based on settting.
 * - This is to enable pushing colour ('Farbe') to the last position.
 * @param {Object} a - Option A.
 * @param {Object} b - Option B.
 * @returns {Number}
 */
export function sortOptionNames(a, b) {
  const indexA = cnvs.product.optionSorting.indexOf(a.name.toLowerCase());
  const indexB = cnvs.product.optionSorting.indexOf(b.name.toLowerCase());

  return indexA === -1 && indexB === -1
    ? a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    : indexA - indexB;
}
